<template>
	<div class="add-role-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="add-role-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="add-role-modal__search-input"
				:placeholder="$t('search_short')"
			/>
			<div
				v-if="props.showCloseIcon"
				class="add-role-modal__close"
			>
				<app-tooltip :text="$t('close_label')">
					<template #activator>
						<app-icon
							name="close"
							width="20px"
							color="#D2D2D2"
							hover-color="#499259"
							cursor="pointer"
							@click="emit('close')"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="add-role-modal__header">
			<div class="add-role-modal__header-title">
				{{ $t('roles_label') }}
			</div>
		</div>
		<div
			class="add-role-modal__body"
			ref="$body"
		>
			<div
				v-for="role in filteredRoles"
				:key="role.Id"
				class="add-role-modal__body-item"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="isRoleAdded(role.Id)"
					text=""
					@input="onChangeRoleStage($event, role)"
				/>
				<div
					class="add-role-modal__body-item-text"
					@click="onChangeRoleStageFromName(role)"
				>
					{{ role.Name }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { ProjectApi } from "@/ api/ProjectApi";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { projectPageStore } from "@/store/project-page";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const props = withDefaults(defineProps<{
	projectId: number,
	entityId: number,
	attachedRoles: { Id: number, Name: string }[],
	target?: 'worker' | 'client',
	showCloseIcon?: boolean,
}>(), {
	target: 'worker',
})

const emit = defineEmits(['close'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = projectPageStore()

const isLoading = ref(true)
const filter = ref('')
const roles = ref<CommonEntityRecord[]>([])

const addedIds = computed(() => new Set((props.attachedRoles || []).map(r => r.Id)))

const filteredRoles = computed<CommonEntityRecord[]>(() => {
	const query = filter.value.trim().toLowerCase()
	if (!query) return roles.value
	return roles.value.filter(r => r.Name?.toLowerCase().includes(query))
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onFetchRoles = async () => {
	try {
		isLoading.value = true
		if (props.target === 'client') {
			roles.value = await ProjectApi.getClientRoles(props.projectId, props.entityId)
		} else {
			roles.value = await ProjectApi.getWorkerRoles(props.projectId, props.entityId)
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const isRoleAdded = (roleId: number) => addedIds.value.has(roleId)

const onAttach = async (role: CommonEntityRecord) => {
	try {
		isLoading.value = true
		if (props.target === 'client') {
			await store.attachRoleToClient(props.entityId, { Id: role.Id, Name: role.Name })
		} else {
			await store.attachRoleToWorker(props.entityId, { Id: role.Id, Name: role.Name })
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDetach = async (roleId: number) => {
	try {
		isLoading.value = true
		if (props.target === 'client') {
			await store.detachRoleFromClient(props.entityId, roleId)
		} else {
			await store.detachRoleFromWorker(props.entityId, roleId)
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onChangeRoleStage = async (stage: boolean, role: CommonEntityRecord) => {
	if (stage) {
		await onAttach(role)
	} else {
		await onDetach(role.Id)
	}
}

const onChangeRoleStageFromName = async (role: CommonEntityRecord) => {
	if (isRoleAdded(role.Id)) {
		await onDetach(role.Id)
	} else {
		await onAttach(role)
	}
}

onMounted(async () => {
	await onFetchRoles()
	await nextTick(() => $input.value?.focusInput())
})
</script>

<style scoped lang="scss">
.add-role-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}
</style>
